import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DemoBadge } from "@/components/PageHeader";
import { BenefitIcon } from "@/components/BenefitIcon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, ArrowRight, ShieldCheck, XCircle, CheckCircle2, FileText, MapPin, Layers,
} from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get(`/products/${id}`), api.get(`/eligibility/${id}`)])
      .then(([p, e]) => { setProduct(p.data); setEligibility(e.data); })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="p-6 sm:p-12 max-w-5xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;
  }
  if (!product) return null;

  const money = (n) => (n == null ? "—" : `$${n.toFixed(2)}`);
  const eligibleMembers = eligibility?.members?.filter((m) => m.eligible) || [];

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-5xl mx-auto animate-rise">
      <Link to={`/app/benefits/${product.benefit_category_key}`} className="inline-flex items-center gap-1 text-sm text-navy-700 hover:text-gold-dark mb-6" data-testid="back-to-category">
        <ArrowLeft className="w-4 h-4" /> Back to {product.category?.name}
      </Link>

      {/* Header */}
      <div className="bg-navy-800 rounded-2xl p-8 mb-6 relative overflow-hidden">
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <BenefitIcon name={product.category?.icon} className="w-7 h-7 text-gold" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-mist-300 text-xs uppercase tracking-wider font-semibold">{product.carrier?.name}</span>
                <DemoBadge />
              </div>
              <h1 className="font-display text-3xl font-bold text-white tracking-tight">{product.name}</h1>
              <p className="text-mist-200 text-sm mt-1">{product.product_type} · Policy form {product.policy_form}</p>
            </div>
          </div>
          <Button onClick={() => navigate(`/app/enroll/${product.id}`)} size="lg" className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold shrink-0" data-testid="start-enrollment">
            Elect this benefit <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="What this benefit does">
            <p className="text-muted-foreground leading-relaxed">{product.plain_language || product.summary}</p>
          </Section>

          <Section title="Coverage options" icon={Layers}>
            <div className="space-y-2">
              {product.coverage_options?.map((o, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-mist-200" data-testid={`coverage-option-${i}`}>
                  <div>
                    <p className="font-semibold text-navy-900 text-sm">{o.label}</p>
                    {o.coverage_amount && <p className="text-xs text-muted-foreground">Benefit: ${o.coverage_amount.toLocaleString()}</p>}
                  </div>
                  <p className="font-display font-bold text-navy-900">{money(o.monthly_premium)}<span className="text-xs text-muted-foreground font-sans">/mo</span></p>
                </div>
              ))}
            </div>
          </Section>

          {product.riders?.length > 0 && (
            <Section title="Included features & riders">
              <div className="flex flex-wrap gap-2">
                {product.riders.map((r) => (
                  <span key={r} className="inline-flex items-center gap-1 text-sm bg-mist-100 text-navy-700 px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="w-4 h-4 text-gold-dark" /> {r}
                  </span>
                ))}
              </div>
            </Section>
          )}

          <Section title="Plan details" icon={FileText}>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><dt className="text-muted-foreground">Benefit limits</dt><dd className="text-navy-900 font-medium mt-0.5">{product.benefit_limits || "See plan documents"}</dd></div>
              <div><dt className="text-muted-foreground">Product version</dt><dd className="text-navy-900 font-medium mt-0.5">v{product.version}</dd></div>
            </dl>
          </Section>
        </div>

        {/* Eligibility sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-mist-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-gold-dark" />
              <h3 className="font-display text-lg font-bold text-navy-900">Who's eligible</h3>
            </div>
            <div className="space-y-3">
              {eligibility?.members?.map((m, i) => (
                <div key={i} className="flex items-start gap-2.5" data-testid={`eligibility-${i}`}>
                  {m.eligible
                    ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    : <XCircle className="w-5 h-5 text-mist-300 shrink-0 mt-0.5" />}
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold ${m.eligible ? "text-navy-900" : "text-muted-foreground"}`}>{m.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {m.relationship.replace("_", " ")}
                      {!m.eligible && m.reasons?.length ? ` · ${m.reasons[0]}` : m.eligible ? " · Eligible" : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-mist-100 rounded-xl border border-mist-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-navy-700" />
              <p className="font-semibold text-navy-900 text-sm">Availability</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {product.state_availability?.length
                ? `Available in: ${product.state_availability.join(", ")}`
                : "State availability will be populated from real carrier materials."}
            </p>
          </div>

          <Button onClick={() => navigate(`/app/enroll/${product.id}`)} className="w-full bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold h-11" data-testid="sidebar-elect">
            Elect this benefit
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-xl border border-mist-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-5 h-5 text-gold-dark" />}
        <h3 className="font-display text-lg font-bold text-navy-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}
